// Lib
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, partition } from 'lodash';
import { List } from 'immutable';

// Selectors
import { getSelectedTaskIdSelector } from '../../../selector/selectionToolsSelector';
import { getElement } from '../../../../../element/selectors/elementSelector';

// Utils
import { getElementId, getTags } from '../../../../../../common/elements/utils/elementPropertyUtils';
import {
    addTagToElements,
    createAndAddTagToElementsThunk,
    removeTagFromElements,
} from '../../../../../element/tags/tagsActions';
import { updateTag } from '../../../../../../common/tags/tagActions';

// Types
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { Tag } from '../../../../../../common/tags/tagTypes';
import { TagsToolContentProps } from './TagsToolContent';

type ReturnType = {
    searchString: string;
    setSearchString: (value: string) => void;
    selectedTags: Tag[];
    unselectedTags: Tag[];
    handleClickSelectedTag: (tag: Tag) => void;
    handleAddTagToElement: (tag: Tag) => void;
    handleCreateTag: (rootBoardId?: string) => void;
};

const useTagContentState = (props: TagsToolContentProps): ReturnType => {
    const { currentBoardId, selectedElements, allTags, currentBoardTags, currentUserId, closePopup } = props;
    const [searchString, setSearchString] = useState<string>('');
    const tags = searchString ? allTags : currentBoardTags;

    const elementIds = selectedElements.map(getElementId).toArray();
    const selectedTaskListId = useSelector(getSelectedTaskIdSelector);
    const focusElementIds = selectedTaskListId ? [selectedTaskListId] : elementIds;
    const elements: List<ImMNElement> = useSelector((state) => {
        return List(focusElementIds.map((elementId) => getElement(state, { elementId })));
    });

    const dispatch = useDispatch();
    const handleAddTagToElement = (tag: Tag) => {
        if (!tag.rootBoardIds?.includes(currentBoardId)) {
            dispatch(updateTag(tag.id, { rootBoardIds: [...tag.rootBoardIds, currentBoardId] }));
        }

        dispatch(addTagToElements(elements, tag.id));

        // If the user is filtering the tags close the popup when they select a tag
        if (searchString) closePopup();
    };
    const handleRemoveTagFromElement = (tag: Tag) => dispatch(removeTagFromElements(elements, tag.id));
    const handleClickSelectedTag = (tag: Tag) => {
        const isTagSelectedForEveryElement = elements.every((element) => getTags(element)?.includes(tag.id));
        const updateFunction = isTagSelectedForEveryElement ? handleRemoveTagFromElement : handleAddTagToElement;

        updateFunction(tag);
    };

    const handleCreateTag = (rootBoardId = currentBoardId) => {
        if (!searchString) return;

        const tagData = {
            name: searchString,
            rootBoardIds: [rootBoardId],
            creator: currentUserId,
        };

        dispatch(createAndAddTagToElementsThunk(tagData, selectedElements));

        setSearchString('');
        closePopup();
    };

    const filteredTags = useMemo(() => {
        if (isEmpty(searchString)) return tags;

        const value = searchString!.toLowerCase();
        return tags.filter((tag) => tag.name.toLowerCase().includes(value));
    }, [tags, searchString]);

    const elementTagIds = elements.flatMap((element) => getTags(element) || []);
    const [selectedTags, unselectedTags] = partition(filteredTags, (tag) => elementTagIds?.includes(tag.id));

    return {
        searchString,
        setSearchString,
        selectedTags,
        unselectedTags,
        handleClickSelectedTag,
        handleAddTagToElement,
        handleCreateTag,
    };
};

export default useTagContentState;
